import { colors } from "src/theme/colors";
import { theme } from "src/theme/theme";
import styled, { css } from "styled-components/macro";
import { line, radii, space } from "src/theme/ui";

/**
 * A standing note at the top of a form.
 *
 * It was a full accent tint with no edge, 2em of side padding and its copy at
 * 14px BOLD — a slab of brand colour carrying a paragraph in the weight this
 * product reserves for figures and headings. On this tenant primary[30] is a
 * real brand colour rather than a neutral tint, so the note was the most
 * saturated thing on the page and outshouted the fields under it.
 *
 * A note is not an alarm: a hairline pane in the neutral the dialogs' own
 * panels use, ordinary weight, at the product's own note size. Not a tint —
 * a tinted panel on a white card is the same surface drawn twice, and on
 * this tenant the accent tint is a real brand colour rather than a neutral.
 */
export const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  /* Its own clearance from whatever it introduces. The form rows below it
     carry no top margin — so that the first field does not hang off the top
     of a dialog body — which left the note butting straight onto the first
     label. */
  margin-bottom: ${space.lg}px;
  padding: 12px 14px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.lg}px;
  background: ${colors.gray[25]};

  @media only screen and (max-width: 767px) {
    padding: 12px;
  }
`;

/* The glyph, at full strength on a neutral pane rather than a tinted pane
   behind a filtered bitmap: chroma in a small area, which is how this
   product spends it. The tone is carried HERE and nowhere else — a status
   colour washed across the whole pane turns a note into an alarm. */
export const Mark = styled.span<{ $tone?: "info" | "caution" }>`
  display: inline-flex;
  flex: none;
  margin-top: 1px;
  color: ${({ $tone }) =>
    $tone === "caution" ? colors.warning[500] : colors.primary[500]};
`;

/* The note's own first line: what it is about, in ink, so a reader takes it
   from the note rather than from the sentence that follows. */
export const Lead = styled.span`
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
  color: ${colors.gray[900]};
`;

const WhiteIcon = css`
  filter: invert(100%);
`;

const IconSize = css<{ size?: string }>`
  height: ${({ size }) => size};
  width: ${({ size }) => size};
`;

export const Icon = styled.img<{
  color?: "blue" | "white" | "green";
  size?: string;
}>`
  filter: invert(64%) sepia(48%) saturate(6743%) hue-rotate(101deg)
    brightness(104%) contrast(101%);
  ${({ color }) => color === "white" && WhiteIcon}
  ${({ size }) => size && IconSize}
`;

export const Message = styled.div`
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.55;
  color: ${colors.gray[700]};
`;
